import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import DateInput from "./DateInput";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import FrasarioSelect from "./FrasarioSelect";
import { sediList, emailHeaderHtml } from "../lib/letterPdf";
import {
  BookText, Plus, Trash2, Pencil, Copy, Eye, EyeOff, Printer, Loader2, Check, X, ChevronDown, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";
import { ShareReplyButton, isShareReply } from "./ShareReplyButton";

const TABS = [
  { k: "note", l: "Note / Movimenti" },
  { k: "email_out", l: "Email Inviate" },
  { k: "email_in", l: "Email Ricevute" },
  { k: "promemoria", l: "PROMEMORIA" },
];
const reqFor = (k) => (k === "promemoria" ? { tipo: "promemoria", vista: "" } : { tipo: "diario", vista: k });
const EMPTY = { descrizione: "", data: "", ora: "", visibile_cliente: false };

function printMovimento(m, pratica, soggetto) {
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>Movimento diario</title><meta charset="utf-8"/>
    <style>body{font-family:Georgia,serif;color:#111;padding:48px;line-height:1.6}</style></head>
    <body>
      <div style="font-size:13px;color:#444">Pratica ${pratica?.numero_pratica || ""} · ${soggetto || ""}</div>
      <div style="font-size:13px;color:#444;margin-bottom:16px">Data: ${formatDate(m.data)}${m.ora ? ` · ore ${m.ora}` : ""}</div>
      <div style="font-size:16px">${(m.descrizione || "").replace(/</g, "&lt;")}</div>
    </body></html>`);
  w.document.close(); w.focus();
  setTimeout(() => w.print(), 300);
}

// Estrae i campi strutturati dalla descrizione di una voce email/PEC del diario.
// Toglie dall'anteprima l'intestazione studio (stesso appiattimento del backend: tag → spazio, spazi compressi).
const flatHtml = (s) => String(s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
const escHtml = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
function stripStudioHead(testo, heads) {
  let t = (testo || "").trim();
  for (const h of heads || []) {
    if (h.full && t.startsWith(h.full)) return t.slice(h.full.length).trim();
    let out = t, hit = false;
    for (const part of h.parts) { if (part && out.startsWith(part)) { out = out.slice(part.length).trim(); hit = true; } }
    if (hit) return out;
  }
  return t;
}

function parseMailEntry(m) {
  const parts = (m.descrizione || "").split(" — ");
  const r = { kind: "email", direction: "out", oggetto: "", mittente: "", destinatario: "", testo: "", from: "", tsDate: "", tsTime: "" };
  parts.forEach((pp, idx) => {
    const t = (pp || "").trim();
    if (/^Oggetto:/i.test(t)) r.oggetto = t.replace(/^Oggetto:\s*/i, "");
    else if (/^Mittente:/i.test(t)) r.mittente = t.replace(/^Mittente:\s*/i, "");
    else if (/^Destinatario:/i.test(t)) r.destinatario = t.replace(/^Destinatario:\s*/i, "");
    else if (/^Testo:/i.test(t)) r.testo = t.replace(/^Testo:\s*/i, "");
    else if (/^Da:/i.test(t)) r.from = t.replace(/^Da:\s*/i, "");
    else if (/(Email|PEC)\s+(inviat|ricevut)/i.test(t)) { r.kind = /PEC/i.test(t) ? "pec" : "email"; r.direction = /ricevut/i.test(t) ? "in" : "out"; }
    else {
      const mt = t.match(/^(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2})/);
      if (mt) { r.tsDate = mt[1]; r.tsTime = mt[2]; }
      else if (idx > 0 && !r.oggetto && !/:/.test(t)) r.oggetto = t;
    }
  });
  return r;
}

function KindBadge({ kind }) {
  const pec = kind === "pec";
  return (
    <span data-testid="mail-kind" className={`text-[10px] font-bold uppercase tracking-wider rounded px-2 py-0.5 ${pec ? "bg-violet-500/20 text-violet-300 border border-violet-500/40" : "bg-blue-500/20 text-blue-300 border border-blue-500/40"}`}>
      {pec ? "PEC" : "Email"}
    </span>
  );
}

function MailRecipients({ m, fallback }) {
  const d = m.destinatari || {};
  const rows = [["A", d.to], ["CC", d.cc], ["CCN", d.bcc]].filter(([, l]) => Array.isArray(l) && l.length);
  if (!rows.length && fallback) rows.push(["A", fallback.split(/\s*,\s*/)]);
  if (!rows.length) return null;
  return (
    <div className="mt-1 space-y-0.5 max-w-[260px] ml-auto" data-testid={`mail-recipients-${m.id}`}>
      {rows.map(([k, l]) => (
        <div key={k} className="text-xs text-slate-300 break-all" data-testid={`mail-rcpt-${k.toLowerCase()}-${m.id}`}>
          <span className="text-slate-500 font-mono uppercase mr-1">{k}:</span>{l.join(", ")}
        </div>
      ))}
    </div>
  );
}

function EmailEntry({ m, direction, onDel, onPrint, onToggleVis, heads }) {
  const [open, setOpen] = useState(false);
  const p = parseMailEntry(m);
  if (direction === "out") p.testo = stripStudioHead(p.testo, heads);
  const dateStr = m.data ? formatDate(m.data) : (p.tsDate || "");
  const timeStr = m.ora || p.tsTime || "";
  const sender = p.mittente || (direction === "in" ? p.from : "");
  const recipient = p.destinatario || "";
  return (
    <div className="p-4 border-b border-slate-800 last:border-0" data-testid={`mail-entry-${m.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <KindBadge kind={p.kind} />
          {sender ? <div className="text-sm text-slate-300 break-words" data-testid={`mail-from-${m.id}`}><span className="text-slate-500 text-[11px] uppercase font-mono mr-1.5">Da</span>{sender}</div> : null}
          {recipient ? <div className="text-sm text-slate-300 break-words" data-testid={`mail-to-${m.id}`}><span className="text-slate-500 text-[11px] uppercase font-mono mr-1.5">A</span>{recipient}</div> : null}
          {p.oggetto ? <div className="text-sm font-bold text-slate-100 break-words pt-0.5" data-testid={`mail-subject-${m.id}`}>{p.oggetto}</div> : null}
          {m.lettera ? <div className="text-xs font-medium break-words flex items-center gap-1" style={{ color: "#b45309" }} data-testid={`mail-lettera-${m.id}`}><span className="text-slate-500 text-[10px] uppercase font-mono">Lettera</span>{m.lettera}</div> : null}
        </div>
        <div className="text-right shrink-0" data-testid={`mail-date-${m.id}`}>
          <div className="text-lg font-bold text-amber-400 leading-tight font-mono">{dateStr || "—"}</div>
          {timeStr ? <div className="text-sm text-slate-400 font-mono">ore {timeStr}</div> : null}
          {direction === "out" ? <MailRecipients m={m} fallback={recipient} /> : null}
        </div>
      </div>
      {p.testo ? (
        <div className="mt-2">
          {open ? <div className="text-sm text-slate-300 whitespace-pre-wrap bg-slate-900/40 rounded p-3 border border-slate-800 mb-1" data-testid={`mail-body-${m.id}`}>{p.testo}</div> : null}
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`mail-toggle-${m.id}`} className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300">
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} /> {open ? "Nascondi testo" : "Mostra testo completo"}
          </button>
        </div>
      ) : null}
      <div className="mt-2 flex items-center gap-3">
        <button onClick={() => onToggleVis(m)} title={m.visibile_cliente ? "Visibile al cliente" : "Non visibile al cliente"} className={m.visibile_cliente ? "text-emerald-400 hover:text-emerald-300" : "text-red-500 hover:text-red-400"} data-testid={`mail-vis-${m.id}`}>{m.visibile_cliente ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
        <button onClick={() => onPrint(m)} title="Stampa" className="text-slate-400 hover:text-cyan-400" data-testid={`mail-print-${m.id}`}><Printer className="h-4 w-4" /></button>
        <button onClick={() => onDel(m)} title="Elimina" className="text-red-400 hover:text-red-300 ml-auto" data-testid={`mail-del-${m.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function DiarioDialog({ open, onOpenChange, pratica, soggetto, clienteKey = "principale" }) {
  const [tab, setTab] = useState("note");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [counts, setCounts] = useState({ note: 0, email_out: 0, email_in: 0, promemoria: 0 });
  const descRef = useRef(null);
  const [heads, setHeads] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/studio/me").then(({ data }) => setHeads(sediList(data).map((s) => ({
      full: flatHtml(emailHeaderHtml(s)),
      parts: [s.nome, ...(s.lines || [])].map((x) => flatHtml(escHtml(x))).filter(Boolean),
    })))).catch(() => setHeads([]));
  }, [open]);

  const autoGrow = () => { const el = descRef.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } };
  useEffect(() => { autoGrow(); }, [form.descrizione, adding, editingId]);

  const base = `/studio/pratiche/${pratica?.id}/diario`;

  const load = async (k) => {
    setLoading(true);
    try {
      const { tipo, vista } = reqFor(k);
      const { data } = await api.get(`${base}?tipo=${tipo}&vista=${vista}&cliente_id=${encodeURIComponent(clienteKey || "")}&soggetto=${encodeURIComponent(soggetto || "")}`);
      setRows(data);
      setCounts((c) => ({ ...c, [k]: data.length }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadCounts = async () => {
    try {
      const q = `cliente_id=${encodeURIComponent(clienteKey || "")}&soggetto=${encodeURIComponent(soggetto || "")}`;
      const [n, eo, ei, pr] = await Promise.all([
        api.get(`${base}?tipo=diario&vista=note&${q}`),
        api.get(`${base}?tipo=diario&vista=email_out&${q}`),
        api.get(`${base}?tipo=diario&vista=email_in&${q}`),
        api.get(`${base}?tipo=promemoria&vista=&${q}`),
      ]);
      setCounts({ note: n.data.length, email_out: eo.data.length, email_in: ei.data.length, promemoria: pr.data.length });
    } catch { /* non bloccante */ }
  };

  useEffect(() => {
    if (open) { setTab("note"); setSelected({}); setAdding(false); setEditingId(null); setForm(EMPTY); load("note"); loadCounts(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const switchTab = (t) => { setTab(t); setAdding(false); setEditingId(null); setForm(EMPTY); load(t); };

  const startAdd = () => { setEditingId(null); setAdding(true); setForm({ ...EMPTY, data: new Date().toLocaleDateString("en-CA"), ora: new Date().toTimeString().slice(0, 5) }); };
  const startFromFrase = (fr) => { setEditingId(null); setAdding(true); setForm({ descrizione: fr.testo || "", data: new Date().toLocaleDateString("en-CA"), ora: new Date().toTimeString().slice(0, 5), visibile_cliente: !!fr.visibile_cliente }); };
  const startEdit = (m) => { setAdding(false); setEditingId(m.id); setForm({ descrizione: m.descrizione || "", data: (m.data || "").slice(0, 10), ora: m.ora || "", visibile_cliente: !!m.visibile_cliente }); };
  const cancelForm = () => { setAdding(false); setEditingId(null); setForm(EMPTY); };

  const saveForm = async () => {
    if (!form.descrizione.trim()) { toast.error("Inserisci una descrizione"); return; }
    try {
      if (editingId) {
        await api.patch(`${base}/${editingId}`, { descrizione: form.descrizione.trim(), data: form.data || null, ora: form.ora || null, visibile_cliente: form.visibile_cliente });
      } else {
        await api.post(base, { descrizione: form.descrizione.trim(), data: form.data || null, ora: form.ora || null, visibile_cliente: form.visibile_cliente, tipo: (tab === "promemoria" ? "promemoria" : "diario"), soggetto: soggetto || "", cliente_id: clienteKey || "principale" });
      }
      toast.success(editingId ? "Movimento aggiornato" : "Movimento aggiunto");
      cancelForm();
      load(tab);
      loadCounts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (m) => {
    if (!window.confirm("Eliminare questo movimento?")) return;
    try { await api.delete(`${base}/${m.id}`); setRows((x) => x.filter((i) => i.id !== m.id)); loadCounts(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplica = async (m) => {
    try {
      await api.post(base, { descrizione: m.descrizione || "", data: m.data || null, ora: m.ora || null, visibile_cliente: !!m.visibile_cliente, tipo: m.tipo || (tab === "promemoria" ? "promemoria" : "diario"), soggetto: m.soggetto || soggetto || "", cliente_id: m.cliente_id || clienteKey || "principale" });
      toast.success("Nota duplicata");
      load(tab); loadCounts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selIds = rows.filter((r) => selected[r.id]).map((r) => r.id);
  const allSel = rows.length > 0 && selIds.length === rows.length;
  const delSelected = async () => {
    if (!window.confirm(`Eliminare definitivamente ${selIds.length} ${selIds.length === 1 ? "nota selezionata" : "note selezionate"}?`)) return;
    try {
      const { data } = await api.post(`${base}/bulk-delete`, { ids: selIds });
      setRows((x) => x.filter((i) => !selected[i.id])); setSelected({}); loadCounts();
      toast.success(`${data.deleted} ${data.deleted === 1 ? "nota eliminata" : "note eliminate"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleVis = async (m) => {
    try {
      const { data } = await api.patch(`${base}/${m.id}`, { visibile_cliente: !m.visibile_cliente });
      setRows((x) => x.map((i) => (i.id === m.id ? data : i)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-4xl h-[88vh] overflow-hidden flex flex-col" data-testid="diario-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2 text-base" data-testid="diario-title">
            <BookText className="h-5 w-5 text-amber-400" />
            DIARIO PER L'ASSISTITO {(soggetto || "—").toUpperCase()} SULLA PRATICA {pratica?.numero_pratica || "—"}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-800 -mt-1">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => switchTab(t.k)} data-testid={`diario-tab-${t.k}`}
              className={`px-4 py-2 text-xs font-semibold tracking-wide border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-amber-500 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"
              }`}>
              {t.l}{counts[t.k] != null ? ` (${counts[t.k]})` : ""}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto pt-3">
          {/* Add button + form */}
          <div className="mb-3">
            {!adding && !editingId && tab !== "email_out" && tab !== "email_in" && (
              <div className="flex flex-wrap items-center gap-2">
              <Button onClick={startAdd} data-testid="diario-add" className="h-9 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                <Plus className="h-4 w-4 mr-1" /> Nuovo movimento
              </Button>
              {tab === "note" && <FrasarioSelect onPick={startFromFrase} />}
              </div>
            )}
            {(adding || editingId) && (
              <div className="pgp-card p-4 space-y-3" data-testid="diario-form">
                <div className="grid sm:grid-cols-[1fr_150px_110px] gap-3">
                  <div>
                    <label className="text-xs text-slate-400">Descrizione *</label>
                    <Textarea ref={descRef} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="diario-descrizione" rows={3} className="mt-1 min-h-[4.5rem] resize-y overflow-hidden bg-slate-900/60 border-slate-700" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Data</label>
                    <div className="mt-1"><DateInput value={form.data} onChange={(v) => setForm({ ...form, data: v })} testId="diario-data" className="h-9 bg-slate-900/60 border-slate-700" /></div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Orario</label>
                    <input type="time" value={form.ora || ""} onChange={(e) => setForm({ ...form, ora: e.target.value })} data-testid="diario-ora"
                      className="mt-1 h-9 w-full rounded-md bg-slate-900/60 border border-slate-700 text-slate-100 text-sm px-2 focus:outline-none focus:ring-1 focus:ring-amber-500/40" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer w-fit">
                  <Checkbox checked={form.visibile_cliente} onCheckedChange={(v) => setForm({ ...form, visibile_cliente: !!v })} data-testid="diario-visibile" />
                  Visibile al cliente
                </label>
                <div className="flex gap-2">
                  <Button onClick={saveForm} data-testid="diario-save" className="h-9 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Check className="h-4 w-4 mr-1" /> Salva</Button>
                  <Button onClick={cancelForm} variant="ghost" className="h-9 text-slate-400"><X className="h-4 w-4 mr-1" /> Annulla</Button>
                </div>
              </div>
            )}
          </div>

          {/* List */}
          <div className="pgp-card overflow-hidden">
            {loading ? (
              <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">Nessuna voce in questo elenco.</div>
            ) : (tab === "email_out" || tab === "email_in") ? (
              <div data-testid="mail-list">
                {rows.map((m) => (
                  <EmailEntry key={m.id} m={m} direction={tab === "email_in" ? "in" : "out"} onDel={del} onPrint={(x) => printMovimento(x, pratica, soggetto)} onToggleVis={toggleVis} heads={heads} />
                ))}
              </div>
            ) : (
              <>
              <div className="flex items-center justify-end gap-3 px-3 py-2 border-b border-slate-800 min-h-[44px]">
                {selIds.length > 0 && (
                  <Button onClick={delSelected} data-testid="diario-del-selected" className="h-8 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina selezionate ({selIds.length})
                  </Button>
                )}
                <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                  Seleziona tutte
                  <Checkbox checked={allSel} onCheckedChange={(v) => setSelected(v ? Object.fromEntries(rows.map((r) => [r.id, true])) : {})} data-testid="diario-check-all" />
                </label>
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-800">
                  {rows.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-800/30" data-testid={`diario-row-${m.id}`}>
                      <td className="px-2 py-2.5 w-8">
                        <button onClick={() => del(m)} data-testid={`diario-del-${m.id}`} title="Elimina" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                      </td>
                      <td className="px-2 py-2.5 w-8">
                        <button onClick={() => startEdit(m)} data-testid={`diario-edit-${m.id}`} title="Modifica" className="text-slate-400 hover:text-amber-400"><Pencil className="h-4 w-4" /></button>
                      </td>
                      <td className="px-3 py-2.5 text-slate-100">{m.descrizione}{isShareReply(m) && <div><ShareReplyButton entry={m} praticaId={pratica?.id} onSent={() => { load(tab); loadCounts(); }} /></div>}</td>
                      <td className="px-3 py-2.5 text-slate-400 font-mono text-xs whitespace-nowrap w-28">{formatDate(m.data)}{m.ora ? ` · ${m.ora}` : ""}</td>
                      <td className="px-2 py-2.5 w-14">
                        <div className="flex items-center gap-1.5">
                        <button onClick={() => toggleVis(m)} data-testid={`diario-vis-${m.id}`} title={m.visibile_cliente ? "Visibile al cliente" : "Non visibile al cliente"}
                          className={m.visibile_cliente ? "text-emerald-400 hover:text-emerald-300" : "text-red-500 hover:text-red-400"}>
                          {m.visibile_cliente ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        {m.visibile_cliente && m.letto_cliente_at && (
                          <span title={`Letta dal cliente il ${formatDate(m.letto_cliente_at.slice(0, 10))} alle ${new Date(m.letto_cliente_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`}
                            data-testid={`diario-letto-${m.id}`} className="text-sky-400"><CheckCheck className="h-4 w-4" strokeWidth={2.5} /></span>
                        )}
                        </div>
                      </td>
                      <td className="px-2 py-2.5 w-8">
                        <button onClick={() => duplica(m)} data-testid={`diario-dup-${m.id}`} title="Duplica" className="text-slate-400 hover:text-amber-400"><Copy className="h-4 w-4" /></button>
                      </td>
                      <td className="px-2 py-2.5 w-8">
                        <button onClick={() => printMovimento(m, pratica, soggetto)} data-testid={`diario-print-${m.id}`} title="Stampa" className="text-slate-400 hover:text-cyan-400"><Printer className="h-4 w-4" /></button>
                      </td>
                      <td className="px-2 py-2.5 w-8">
                        <Checkbox checked={!!selected[m.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [m.id]: !!v }))} data-testid={`diario-check-${m.id}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
